import Vue from 'vue';
import DgdMockDataLoader from './DgdMockDataLoader';
import IdsFilterGenerator from './IdsFilterGenerator';

const DgdDataLoader = {
  /** base URL for API */
  baseUrl: 'dgd/pragdb.geo',

  /**
   * Returns a list of all location types (Ortstypen) as JSON array.
   *
   * @returns The JSON array with all location types (key: 'v_ortstyp')
   */
  async getLocationTypeList () {
    // for dev return mock data
    if (Vue.prototype.$devMode === true) {
      return DgdMockDataLoader.getLocationTypeList();
    }

    const endpoint = 'get_ortstypen_list';
    const resp = await fetch(`${this.baseUrl}.${endpoint}`, {});

    if (resp.status === 200) {
      return resp.json();
    }
  },

  /**
   * Returns a list of all corpora (Korpora) as JSON array.
   *
   * @returns The GeoJSON for the given filter parameters
   */
  async getCorpusList () {
    // for dev return mock data
    if (Vue.prototype.$devMode === true) {
      return DgdMockDataLoader.getCorpusList();
    }

    const endpoint = 'get_corpus_list';
    const resp = await fetch(`${this.baseUrl}.${endpoint}`, {});

    if (resp.status === 200) {
      return resp.json();
    }
  },

  /**
   * Returns a list of all countries as JSON array.
   *
   * @returns The country JSON array
   */
  async getCountryList (locationType) {
    // for dev return mock data
    if (Vue.prototype.$devMode === true) {
      return DgdMockDataLoader.getCountryList();
    }

    const locTypeFilter = IdsFilterGenerator.createLocationTypeFilter(locationType);
    const endpoint = `get_country_list?${locTypeFilter}`;
    const resp = await fetch(`${this.baseUrl}.${endpoint}`, {});

    if (resp.status === 200) {
      const countries = await resp.json();

      // we ensure a client ID for all object to get a harmonized structure
      const modCountries = [];
      countries.forEach(r => {
        modCountries.push({
          // TODO mixed up props in API => switch when fixed in API
          name: r.Langname,
          iso: r['ISO-Code'],
          clientId: r['ISO-Code']
        })
      });

      modCountries.unshift({
        name: '- Keine Auswahl -',
        clientId: null
      });

      return modCountries;
    }
  },

  /**
   * Returns a list of all regions for the given country as JSON array.
   *
   * @returns The region JSON array
   */
  async getRegionList (countryCode, locationType) {
    // for dev return mock data
    if (Vue.prototype.$devMode === true) {
      return DgdMockDataLoader.getRegionList(countryCode);
    }

    const locTypeFilter = IdsFilterGenerator.createLocationTypeFilter(locationType);
    const endpoint = `get_region_list?v_iso=${countryCode}&${locTypeFilter}`;
    const resp = await fetch(`${this.baseUrl}.${endpoint}`, {});

    if (resp.status === 200) {
      const regions = await resp.json();

      const modRegions = [];
      regions.forEach(r => {
        modRegions.push({
          name: r.Region,
          clientId: r.Region
        })
      });

      modRegions.unshift({
        name: '- Keine Auswahl -',
        clientId: null
      })

      return modRegions;
    }
  },

  /**
   * Returns a list of all districts for the given country and region as
   * JSON array.
   *
   * @returns The region JSON array
   */
  async getDistrictList (countryCode, regionName, locationType) {
    // for dev return mock data
    if (Vue.prototype.$devMode === true) {
      return DgdMockDataLoader.getDistrictList(countryCode, regionName);
    }

    const locTypeFilter = IdsFilterGenerator.createLocationTypeFilter(locationType);
    const endpoint = `get_landkreis_list?${locTypeFilter}&v_iso=${countryCode}&v_region_name=${regionName}`;
    const resp = await fetch(`${this.baseUrl}.${endpoint}`, {});

    if (resp.status === 200) {
      const districts = await resp.json();

      const modDistricts = [];
      districts.forEach(d => {
        modDistricts.push({
          name: d.Landkreis,
          clientId: d.Landkreis
        })
      });

      modDistricts.unshift({
        name: '- Keine Auswahl -',
        clientId: null
      });

      return modDistricts;
    }
  },

  /**
   * Returns a list of locations (Ortsdaten) as GeoJSON FeatureCollection.
   *
   * @param {String} corpusSelection
   * @param {String} locationType
   * @returns The GeoJSON for the given filter parameters
   */
  async getLocationList (corpusSelection, locationType) {
    // for dev return mock data
    if (Vue.prototype.$devMode === true) {
      return DgdMockDataLoader.getLocationList();
    }

    const endpoint = 'get_location_list';
    const filterQueryString = IdsFilterGenerator.createFilter(locationType, corpusSelection);

    const resp = await fetch(`${this.baseUrl}.${endpoint}?${filterQueryString}`, {});

    if (resp.status === 200) {
      return resp.json();
    }
  },

  /**
   * Returns a list of locations (Ortsdaten) as GeoJSON FeatureCollection,
   * based on a geographic filter.
   *
   * @param {Object} geoFilter
   * @param {String} locationType
   * @returns The GeoJSON for the given filter parameters
   */
  async getLocationListGeo (geoFilter, locationType) {
    // for dev return mock data
    if (Vue.prototype.$devMode === true) {
      return DgdMockDataLoader.getLocationListGeo();
    }

    const endpoint = 'get_location_list_geographic';
    const filterQueryString = IdsFilterGenerator.createFilter(locationType, null, geoFilter);

    const resp = await fetch(`${this.baseUrl}.${endpoint}?${filterQueryString}`, {});
    // TODO consider catching errors

    return resp.json();
  }
};

export default DgdDataLoader;



// WEBPACK FOOTER //
// ./app/ids/DgdDataLoader.js